import React, { useState } from 'react';
import { Card, Alert, Spinner, Badge } from 'react-bootstrap';
import { Review, ReviewStatus } from '../../Data/Reviews';
import { reviewService } from '../../../components/Review/services/reviewService';
import AdminReviewTable from '../AdminReviewTable/AdminReviewTable';
import './ReviewManagement.css';

interface ReviewManagementProps {
  // Data passed from parent
  reviews: Review[];
  loading?: boolean;
  error?: string | null;
  // Configuration
  title?: string;
  description?: string;
  emptyMessage?: string;
  showTrailInfo?: boolean;  // Whether to show trail column (for pending reviews)
  // Behavior flags
  removeOnAccept?: boolean;  // Remove from list when accepted (for pending-only views)
  removeOnReject?: boolean;  // Remove from list when rejected (for pending-only views)
  // Callbacks
  onReviewsChange?: (reviews: Review[]) => void;
  onRefetch?: () => Promise<void>;  // Optional refetch callback to ensure data consistency
}

const ReviewManagement: React.FC<ReviewManagementProps> = ({
  reviews,
  loading = false,
  error = null,
  title = 'Reviews Management',
  description = 'Manage reviews',
  emptyMessage = 'No reviews found.',
  showTrailInfo = false,
  removeOnAccept = false,
  removeOnReject = false,
  onReviewsChange,
  onRefetch
}) => {
  const [localReviews, setLocalReviews] = useState<Review[]>(reviews);

  // Update local state when props change
  React.useEffect(() => {
    setLocalReviews(reviews);
  }, [reviews]);

  const handleAcceptReview = async (reviewId: number): Promise<boolean> => {
    try {
      const response = await reviewService.acceptReview(reviewId);
      if (response.status === 200) {
        // Refetch data to ensure consistency (rating recalculation, etc.)
        if (onRefetch) {
          await onRefetch();
        } else {
          // Fallback: optimistic update if no refetch provided
          let updatedReviews;
          if (removeOnAccept) {
            // Remove from list (for pending-only views)
            updatedReviews = localReviews.filter(review => review.id !== reviewId);
          } else {
            // Update status in current list
            updatedReviews = localReviews.map(review =>
              review.id === reviewId
                ? { ...review, status: ReviewStatus.Active }
                : review
            );
          }
          setLocalReviews(updatedReviews);
          onReviewsChange?.(updatedReviews);
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error accepting review:', error);
      return false;
    }
  };

  const handleRejectReview = async (reviewId: number): Promise<boolean> => {
    try {
      const response = await reviewService.rejectReview(reviewId);
      if (response.status === 200) {
        // Refetch data to ensure consistency (rating recalculation, etc.)
        if (onRefetch) {
          await onRefetch();
        } else {
          // Fallback: optimistic update if no refetch provided
          let updatedReviews;
          if (removeOnReject) {
            // Remove from list (for pending-only views)
            updatedReviews = localReviews.filter(review => review.id !== reviewId);
          } else {
            // Update status in current list
            updatedReviews = localReviews.map(review =>
              review.id === reviewId
                ? { ...review, status: ReviewStatus.Deleted }
                : review
            );
          }
          setLocalReviews(updatedReviews);
          onReviewsChange?.(updatedReviews);
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error rejecting review:', error);
      return false;
    }
  };

  const getReviewStats = () => {
    const totalReviews = localReviews.length;
    const pendingCount = localReviews.filter(r => r.status === ReviewStatus.Pending).length;
    const activeCount = localReviews.filter(r => r.status === ReviewStatus.Active).length;
    const deletedCount = localReviews.filter(r => r.status === ReviewStatus.Deleted).length;

    return { totalReviews, pendingCount, activeCount, deletedCount };
  };

  const stats = getReviewStats();

  if (error) {
    return (
      <Alert variant="danger" className="mb-4">
        {error}
      </Alert>
    );
  }

  return (
    <div className="review-management">
      <div className="review-header mb-4">
        <h2>{title}</h2>
        <p className="text-muted">{description}</p>

        <div className="review-stats mb-3">
          <Badge bg="primary" className="me-2">
            Total: {stats.totalReviews}
          </Badge>
          <Badge bg="warning" className="me-2">
            Pending: {stats.pendingCount}
          </Badge>
          <Badge bg="success" className="me-2">
            Active: {stats.activeCount}
          </Badge>
          <Badge bg="secondary" className="me-2">
            Deleted: {stats.deletedCount}
          </Badge>
        </div>
      </div>

      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading reviews...</span>
              </Spinner>
              <p className="mt-2 text-muted">Loading reviews...</p>
            </div>
          ) : (
            <AdminReviewTable
              reviews={localReviews}
              loading={loading}
              onAcceptReview={handleAcceptReview}
              onRejectReview={handleRejectReview}
              emptyMessage={emptyMessage}
              showTrailInfo={showTrailInfo}
            />
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default ReviewManagement;